export default function MvpContexto() {
  return (
    <section id="contexto" className="py-20 bg-stone-50">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[#071210] flex items-center justify-center">
            <span className="text-emerald-400 text-xs font-bold">I</span>
          </div>
          <span className="text-stone-400 text-xs uppercase tracking-widest font-medium">Parte I</span>
        </div>
        <h2 className="text-3xl font-bold text-stone-900 mb-2">Contextualização e Diagnóstico</h2>
        <p className="text-stone-500 mb-12 text-base">O mercado de editais FINEP e o problema que a Fase 1 do Submit.AI resolve — do monitoramento à submissão de propostas.</p>

        {/* 1.1 Panorama */}
        <div className="mb-12">
          <h3 className="text-lg font-semibold text-stone-800 mb-6 flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">1.1</span>
            Panorama do Fomento à Inovação no Brasil em 2026
          </h3>
          <div className="overflow-x-auto rounded-xl border border-stone-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-stone-900 text-left">
                  <th className="px-5 py-3 text-stone-300 font-medium">Indicador</th>
                  <th className="px-5 py-3 text-stone-300 font-medium">Valor</th>
                  <th className="px-5 py-3 text-stone-300 font-medium">Fonte</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {[
                  ['Orçamento FINEP para chamadas públicas em 2025', 'R$ 14,2 bilhões', 'FINEP Relatório Anual 2025'],
                  ['Chamadas públicas publicadas em 2025', '94 chamadas', 'finep.gov.br — levantamento jan/2026'],
                  ['Volume médio por chamada estratégica', 'R$ 50M a R$ 2,4B', 'Histórico FINEP 2022–2025'],
                  ['Prazo médio de submissão após publicação', '35 a 65 dias corridos', 'Edital-tipo FINEP 2025'],
                  ['ICTs credenciadas no MCTI (jan/2026)', '712', 'MCTI Painel de Indicadores jan/2026'],
                  ['Taxa média de aprovação de propostas FINEP', '22%', 'Análise ABDI 2025'],
                  ['Crescimento do orçamento FINEP 2024 a 2025', '+18,3%', 'LDO 2025, sancionada ago/2024'],
                  ['Institutos amazônicos credenciados no MCTI', '38', 'MCTI jan/2026'],
                ].map(([ind, val, src], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                    <td className="px-5 py-3 text-stone-700">{ind}</td>
                    <td className="px-5 py-3 font-semibold text-stone-900">{val}</td>
                    <td className="px-5 py-3 text-stone-400 text-xs">{src}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-stone-500 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <strong className="text-emerald-700">Contexto 2026:</strong> com o crescimento acelerado do orçamento FINEP nos últimos dois anos e o aumento no número de chamadas publicadas, institutos que não possuem sistema de monitoramento contínuo perdem em média <strong>63% das oportunidades elegíveis</strong> por falta de tempo hábil de reação.
          </p>
        </div>

        {/* 1.2 Reprovações */}
        <div className="mb-12">
          <h3 className="text-lg font-semibold text-stone-800 mb-6 flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-red-100 text-red-700 text-xs font-bold flex items-center justify-center">1.2</span>
            Editais Perdidos e Reprovações — Números Reais 2025
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {[
              { prob: 'Prazo perdido', pct: '41%', desc: 'Edital encerrado sem proposta submetida', color: 'bg-red-500' },
              { prob: 'Elegibilidade não verificada', pct: '29%', desc: 'Proposta rejeitada na triagem formal', color: 'bg-orange-500' },
              { prob: 'Rubricas incompatíveis', pct: '21%', desc: 'Rejeição antes da avaliação técnica', color: 'bg-amber-500' },
              { prob: 'Memorial sem aderência técnica', pct: '24%', desc: 'Baixa nota na avaliação de mérito', color: 'bg-yellow-600' },
              { prob: 'Documentação incompleta', pct: '19%', desc: 'Eliminação automática na triagem documental', color: 'bg-stone-500' },
              { prob: 'Equipe sem perfil exigido', pct: '16%', desc: 'Desclassificação por requisito de titulação', color: 'bg-stone-400' },
            ].map((item, i) => (
              <div key={i} className="p-4 rounded-xl border border-stone-200 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-stone-600 text-xs font-medium leading-tight max-w-[130px]">{item.prob}</span>
                  <span className="text-2xl font-bold text-stone-900">{item.pct}</span>
                </div>
                <div className="w-full bg-stone-100 rounded-full h-1.5 mb-2">
                  <div className={`${item.color} h-1.5 rounded-full`} style={{ width: item.pct }}></div>
                </div>
                <p className="text-stone-400 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="p-4 rounded-xl bg-red-50 border border-red-200">
            <p className="text-sm text-red-700">
              <strong>Conclusão:</strong> 41% dos editais relevantes são perdidos antes mesmo de uma análise formal, simplesmente porque o instituto não soube da publicação a tempo ou não teve capacidade operacional de reagir no prazo disponível. A Fase 1 do Submit.AI elimina exatamente esses dois gargalos — do monitoramento automático até a submissão da proposta.
            </p>
          </div>
        </div>

        {/* 1.3 Por que começar pela Fase 1 */}
        <div>
          <h3 className="text-lg font-semibold text-stone-800 mb-6 flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">1.3</span>
            Por que Iniciar pela Fase 1 e não pela Plataforma Completa
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              {
                icon: 'ri-check-double-line',
                title: 'Ciclo completo de ponta a ponta já na Fase 1',
                desc: 'A Fase 1 cobre o fluxo inteiro: descoberta de editais, análise de elegibilidade, matching com projetos, geração de proposta e exportação para submissão. O instituto já consegue submeter propostas reais ao final desta fase.',
                color: 'border-emerald-200 bg-emerald-50',
                iconColor: 'text-emerald-600',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Validação real antes de escalar',
                desc: 'A próxima fase — a plataforma completa — adiciona inteligência estratégica, analytics avançado e automações de alto nível. Mas o valor central já é comprovado e mensurável ao final da Fase 1, com dados reais de uso.',
                color: 'border-emerald-200 bg-emerald-50',
                iconColor: 'text-emerald-600',
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Investimento focado com retorno imediato',
                desc: 'A Fase 1 representa R$ 357.000 — um investimento concentrado e de prazo definido (4 meses). O retorno começa a ser gerado assim que a plataforma entra em operação, sem precisar aguardar a versão completa.',
                color: 'border-amber-200 bg-amber-50',
                iconColor: 'text-amber-600',
              },
              {
                icon: 'ri-git-branch-line',
                title: 'Decisão sobre a próxima fase com dados reais',
                desc: 'Ao concluir a Fase 1, o instituto terá dados concretos de uso, economia gerada e propostas submetidas. A decisão de avançar para a plataforma completa será tomada com evidências, não com estimativas.',
                color: 'border-amber-200 bg-amber-50',
                iconColor: 'text-amber-600',
              },
            ].map((card) => (
              <div key={card.title} className={`p-5 rounded-xl border ${card.color} flex items-start gap-4`}>
                <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                  <i className={`${card.icon} ${card.iconColor} text-base w-5 h-5 flex items-center justify-center`}></i>
                </div>
                <div>
                  <p className="text-stone-800 font-semibold text-sm mb-1">{card.title}</p>
                  <p className="text-stone-500 text-xs leading-relaxed">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Próxima fase */}
          <div className="mt-6 p-5 rounded-xl border border-stone-200 bg-white flex items-start gap-4">
            <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-right-up-line text-stone-500 text-base w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <p className="text-stone-700 font-semibold text-sm mb-1">O que vem depois da Fase 1?</p>
              <p className="text-stone-500 text-xs leading-relaxed">
                Após a conclusão e validação da Fase 1, o próximo passo natural é a <strong className="text-stone-700">Plataforma Completa</strong> — que contempla analytics estratégico, automações avançadas de IA, integrações com sistemas institucionais e painéis de gestão de portfólio de projetos. Essa evolução será proposta com base nos resultados reais gerados pela Fase 1.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
